import { styled, keyframes } from '@mui/material/styles';
import { Box, Typography, Button, Select, IconButton, TextField, Switch } from '@mui/material';

// ============================================
// Design System Colors (from Figma)
// ============================================
const COLORS = {
  primary: '#4AB3BC',
  primaryDark: '#3E9FA7',
  primaryLight: '#7DC8CF',
  primaryBg: '#F3F9FA',
  white: '#FFFFFF',
  gray1: '#F2F2F2',
  gray2: '#D9D9D9',
  gray4: '#A6A6A6',
  gray6: '#737373',
  gray8: '#404040',
  gray9: '#262626',
  black: '#141414',
  error: '#DC2626',
};

// ============================================
// Error Animation
// ============================================
export const errorPulseGlow = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.4);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(220, 38, 38, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(220, 38, 38, 0);
  }
`;

export const ErrorFieldWrapper = styled(Box, {
  shouldForwardProp: prop => prop !== 'hasError' && prop !== 'fullWidth',
})(({ hasError, fullWidth }) => ({
  display: 'inline-flex',
  borderRadius: '4px',
  ...(fullWidth && {
    flex: 1,
    '& > *': {
      width: '100%',
    },
  }),
  ...(hasError && {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#dc2626 !important',
      borderWidth: '2px !important',
    },
    animation: `${errorPulseGlow} 1.5s ease-in-out infinite`,
  }),
}));

// ============================================
// Container & Layout
// ============================================

export const PageContainer = styled(Box)({
  background: '#fff',
  borderRadius: '8px',
  width: '100%',
});

export const Header = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '24px',
  borderBottom: 'none',
});

export const HeaderTitle = styled(Typography)({
  fontSize: '18px',
  marginLeft: '8px',
  fontWeight: 550,
});

export const HeaderLink = styled('a')({
  fontSize: 14,
  fontWeight: 500,
  color: '#0F7F85',
  textDecoration: 'none',
});

export const Section = styled(Box)({
  padding: '0 24px',
  marginBottom: '32px',
});

export const SectionTitle = styled(Typography)({
  fontSize: 16,
  fontWeight: 600,
  color: '#111',
  marginBottom: '20px',
});

export const SectionBox = styled(Box)({
  border: '1px solid #e5e5e5',
  borderRadius: '8px',
  padding: '20px 24px',
  marginTop: '16px',
});

export const SectionBoxHeader = styled(Box)({
  marginBottom: '16px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
});

// ============================================
// Form Layout
// ============================================

export const FormRowLeft = styled(Box)({
  display: 'flex',
  width: '100%',
  minHeight: 44,
  marginBottom: '16px',
  gap: '48px',
});

export const FormRowRight = styled(Box)({
  display: 'flex',
  width: '100%',
  minHeight: 44,
  marginBottom: '16px',
  gap: '48px',
});

export const FormRow = styled(Box)({
  display: 'flex',
  width: '100%',
  minHeight: 44,
  marginBottom: '16px',
  gap: '48px',
});

export const FormGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flex: '1 1 0',
  minWidth: 0,
  '& > .MuiTextField-root, & > .MuiFormControl-root, & > .MuiAutocomplete-root': {
    flex: 1,
  },
});

// 50% 너비를 차지하는 FormGroup (수업유형+수업시간 등)
export const FormGroupHalf = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flex: '0 0 calc(50% - 24px)',
  minWidth: 0,
});

export const FormLabel = styled(Typography)({
  fontSize: 13,
  color: '#111',
  width: 56,
  minWidth: 56,
  flexShrink: 0,
  whiteSpace: 'nowrap',
});

export const FormLabelSmall = styled(Typography)({
  fontSize: 13,
  fontWeight: 500,
  color: '#262626',
  whiteSpace: 'nowrap',
  flexShrink: 0,
});

export const FormSubLabel = styled(Typography)({
  fontSize: 14,
  fontWeight: 500,
  color: '#262626',
  marginBottom: '8px',
});

export const HintText = styled(Typography)({
  fontSize: 14,
  color: '#a6a6a6',
  fontWeight: 500,
});

export const UnitText = styled(Typography)({
  fontSize: 13,
  color: '#111',
  flexShrink: 0,
});

// ============================================
// Radio & Checkbox
// ============================================

export const RadioWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  cursor: 'pointer',
});

export const RadioCircle = styled(Box, {
  shouldForwardProp: prop => prop !== 'checked',
})(({ checked }) => ({
  width: 24,
  height: 24,
  borderRadius: '50%',
  border: checked ? '2px solid #0F7F85' : '1px solid #d9d9d9',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#fff',
}));

export const RadioDot = styled(Box)({
  width: 12,
  height: 12,
  borderRadius: '50%',
  backgroundColor: '#0F7F85',
});

export const CheckboxWrapper = styled(Box, {
  shouldForwardProp: prop => prop !== 'checked',
})(({ checked }) => ({
  width: 20,
  height: 20,
  border: '1.5px solid',
  borderColor: checked ? '#0F7F85' : '#d9d9d9',
  borderRadius: '2px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: checked ? '#0F7F85' : '#fff',
}));

// ============================================
// Day Selection
// ============================================

export const DayButton = styled(Box, {
  shouldForwardProp: prop => prop !== 'selected',
})(({ selected }) => ({
  width: 70,
  height: 44,
  border: '1px solid',
  borderColor: selected ? '#0F7F85' : '#d9d9d9',
  borderRadius: '4px',
  background: selected ? '#ECF7F8' : '#fff',
  fontSize: 13,
  fontWeight: 500,
  color: selected ? '#262626' : '#a6a6a6',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.2s ease',
  flexShrink: 0,
  '&:hover': {
    borderColor: '#0F7F85',
  },
}));

// ============================================
// Time Selection
// ============================================

export const TimeSelect = styled(Select)({
  minWidth: 72,
  height: 44,
  flexShrink: 0,
});

export const TimeSeparator = styled(Typography)({
  fontSize: 16,
  fontWeight: 500,
  color: '#262626',
  width: 12,
  textAlign: 'center',
});

// ============================================
// Product Card
// ============================================

export const ProductCard = styled(Box)({
  width: 240,
  height: 88,
  border: '1px solid #0F7F85',
  borderRadius: '8px',
  padding: '16px',
  background: '#fff',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
});

export const ProductBadge = styled('span')({
  background: '#6FB6B3',
  color: '#fff',
  fontSize: 12,
  padding: '4px 8px',
  borderRadius: '99px',
  fontWeight: 500,
});

export const ProductName = styled(Typography)({
  fontSize: 14,
  color: '#111',
});

export const ProductPrice = styled(Typography)({
  fontSize: 14,
  color: '#666',
});

// ============================================
// Buttons
// ============================================

export const PrimaryButton = styled(Button)({
  minWidth: 100,
  height: 40,
  border: 'none',
  borderRadius: '8px',
  background: '#0F7F85',
  color: '#fff',
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
  padding: '0 24px',
  '&:hover': {
    background: '#0a6a70',
  },
  '&:disabled': {
    background: '#ccc',
  },
});

export const SecondaryButton = styled(Button)({
  height: '40px',
  border: '1px solid #0F7F85',
  borderRadius: '8px',
  background: '#fff',
  color: '#0F7F85',
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
  '&:hover': {
    background: '#f5f5f5',
  },
});

export const CancelButton = styled(Button)({
  minWidth: 100,
  height: 40,
  border: 'none',
  borderRadius: '8px',
  background: '#E07979',
  color: '#fff',
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
  padding: '0 24px',
  '&:hover': {
    background: '#c96666',
  },
});

export const UploadButton = styled(Button)({
  minWidth: 113,
  height: 38,
  background: '#0F7F85',
  color: '#fff',
  fontSize: 14,
  borderRadius: '4px',
  '&:hover': {
    background: '#0a6a70',
  },
});

// ============================================
// Image Upload
// ============================================

export const ImagePreviewContainer = styled(Box)({
  display: 'flex',
  gap: '16px',
  flexWrap: 'wrap',
  marginBottom: '16px',
});

export const ImagePreviewWrapper = styled(Box)({
  position: 'relative',
  width: 240,
  height: 180,
});

export const ImagePreview = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  borderRadius: '8px',
});

export const ImageRemoveButton = styled(IconButton)({
  position: 'absolute',
  top: 4,
  right: 4,
  background: 'rgba(0,0,0,0.5)',
  color: '#fff',
  width: 24,
  height: 24,
  '&:hover': {
    background: 'rgba(0,0,0,0.7)',
  },
});

export const ImageUploadBox = styled(Box)({
  width: 100,
  height: 100,
  border: '1px dashed #ddd',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  '&:hover': {
    borderColor: '#0F7F85',
  },
});

// ============================================
// Curriculum
// ============================================

export const CurriculumItem = styled(Box)({
  display: 'flex',
  gap: '24px',
  padding: '24px',
  background: '#F2F2F2',
});

export const CurriculumInput = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    height: 49,
  },
});

// ============================================
// AI Toggle
// ============================================

export const AIToggleBox = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  background: '#ECF7F8',
  borderRadius: '8px',
  padding: '12px 16px',
  margin: '0 24px 24px',
});

export const AIToggleLabel = styled(Typography)({
  fontSize: 14,
  fontWeight: 500,
  color: '#0F7F85',
});

// ============================================
// Bottom Actions
// ============================================

export const BottomActions = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '8px',
  padding: '24px',
  marginTop: '32px',
});

// ============================================
// Misc
// ============================================

export const BackButton = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

export const HeaderBackLink = styled(Box)({
  textDecoration: 'none',
  color: '#3a3a3a',
  display: 'flex',
  alignItems: 'center',
});

// Step01 추가 스타일
export const FormRowHalf = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '48px',
  width: '100%',
  minHeight: 44,
  marginBottom: '16px',
});

export const FormGroupFlex = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flex: 1,
  flexWrap: 'nowrap',
});

export const RadioContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '24px',
  marginRight: '120px',
});

export const RadioItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  width: 100,
  height: 24,
  cursor: 'pointer',
});

export const StopSettingContainer = styled(Box)({
  display: 'flex',
  gap: '48px',
  marginBottom: '20px',
});

export const StopSettingLeft = styled(Box)({
  flex: 1,
});

export const StopSettingRight = styled(Box)({
  flex: 1,
  width: '100%',
  minWidth: 0,
});

export const StopInputGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  width: '100%',
});

export const StopInputRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  flex: 1,
});

export const StopCheckboxGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flexShrink: 0,
});

export const DayButtonContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  flexWrap: 'nowrap',
});

export const TimeSettingContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const TimeInputGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});

export const UsageLimitContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

export const SmallInputGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  '& .MuiOutlinedInput-root': {
    width: 120,
  },
});

export const MediumInputGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  '& .MuiOutlinedInput-root': {
    width: 160,
  },
});

export const UsageInfoGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '48px',
  flex: '1 1 0',
  minWidth: 0,
});

export const UsageInputWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flex: '1 1 0',
  minWidth: 0,
  '& .MuiOutlinedInput-root': {
    flex: 1,
  },
});

export const LessonTimeInputWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  '& .MuiOutlinedInput-root': {
    width: 280,
  },
});

export const ReservationCancelRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  width: '100%',
});

export const ReservationCancelRowLeft = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

export const ReservationCancelRowRight = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

export const ReservationBox = styled(Box)({
  display: 'flex',
  flex: 1,
  minWidth: 0,
});

export const ProductSelectWrapper = styled(Box)({
  width: '40%',
  maxWidth: 400,
  marginBottom: 16,
});

export const ProductSelect = styled(Select)({
  width: '100%',
  maxWidth: 400,
  height: 44,
});

export const ProductCardContainer = styled(Box)({
  display: 'flex',
  gap: '24px',
  flexWrap: 'wrap',
  marginTop: '16px',
});

export const ProductCardHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

export const ProductCardBadgeWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});

export const CloseIconWrapper = styled('span')({
  color: '#6FB6B3',
  cursor: 'pointer',
});

export const PriceInputRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  marginBottom: 16,
  gap: '8px',
});

export const DiscountSettingRow = styled(Box)({
  display: 'flex',
  gap: '24px',
  alignItems: 'flex-start',
});

export const DiscountColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  flex: 1,
  minWidth: 0,
});

export const SalePeriodRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  marginTop: 16,
  gap: '48px',
});

export const SalePeriodGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const SalePeriodDateGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  width: '380px',
  '& .MuiFormControl-root': {
    flex: 1,
  },
});

export const UsagePeriodGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  '& .MuiTextField-root': {
    flex: 'none',
  },
  '& > div': {
    flex: 'none',
  },
});

// Step02 추가 스타일
export const AIToggleIconWrapper = styled(Box)({
  width: 24,
  height: 24,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#0F7F85',
});

export const AIToggleLabelWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const ImageUploadFooter = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

export const CurriculumLeftColumn = styled(Box)({
  flex: 1,
});

export const CurriculumRightColumn = styled(Box)({
  flex: 1,
});

export const CurriculumTitleRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '8px',
});

export const CurriculumImageContainer = styled(Box)({
  display: 'flex',
  gap: '8px',
  flexWrap: 'wrap',
});

export const CurriculumImageWrapper = styled(Box)({
  position: 'relative',
  width: 100,
  height: 100,
});

export const CurriculumImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  borderRadius: '8px',
});

export const CurriculumImageRemoveButton = styled(IconButton)({
  position: 'absolute',
  top: 2,
  right: 2,
  background: 'rgba(0,0,0,0.5)',
  color: '#fff',
  width: 20,
  height: 20,
  '&:hover': {
    background: 'rgba(0,0,0,0.7)',
  },
});

export const CurriculumAddRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 24px',
});

export const CurriculumAddButton = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  cursor: 'pointer',
  '&:hover': {
    opacity: 0.7,
  },
});

export const CurriculumInfoWrapper = styled(Box)({
  padding: '0 24px 16px',
});

export const ClassDetailWrapper = styled(Box)({
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  overflow: 'hidden',
});

export const RemoveIconWrapper = styled('span')({
  marginLeft: 'auto',
  color: '#F87171',
  cursor: 'pointer',
});

export const InfoText = styled(Typography)({
  fontSize: 12,
  color: '#999',
});

export const CountText = styled(Typography)({
  fontSize: 13,
  color: '#666',
  marginBottom: '8px',
});

// ============================================
// Edit Mode (Detail View) Styles
// ============================================

export const EditModeHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  padding: '0 24px',
});

export const StatusToggleWrapper = styled(Box)({
  display: 'flex',
  marginTop: '25px',
  marginBottom: '25px',
});

export const StatusToggleLabel = styled(Typography)({
  width: '70px',
  color: '#000',
});

export const TicketInfoWrapper = styled(Box)({
  marginLeft: '40px',
  width: '100%',
});

export const TicketInfoHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

export const TicketInfoTitle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
});

export const StatusBadge = styled(Button)({
  width: 'auto',
  marginLeft: '8px',
  padding: '0',
});

export const CopyButton = styled(Button)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  flexShrink: 0,
  height: '36px',
  padding: '0 16px',
  marginLeft: 'auto',
  borderRadius: '8px',
  background: '#F1F4F6',
  color: '#707070',
  fontSize: '14px',
  fontWeight: 600,
  lineHeight: 1,
  whiteSpace: 'nowrap',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#E4E9ED',
    boxShadow: 'none',
  },
});

export const WishlistWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

export const WishlistIcon = styled('img')({
  width: '16px',
  height: '17px',
});

export const WishlistText = styled(Typography)({
  marginLeft: '5px',
  fontSize: '12px',
});

export const Divider = styled('hr')({
  border: '1px solid #e1e1e1',
});

// Modal Styles
export const ModalContainer = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '420px',
  maxWidth: 'calc(100vw - 32px)',
  backgroundColor: '#FFFFFF',
  borderRadius: '16px',
  border: 'none',
  outline: 'none',
  padding: '32px 28px 24px',
  boxShadow: '0 12px 32px rgba(20, 20, 20, 0.16)',
});

export const ModalCloseIcon = styled(Box)({
  position: 'absolute',
  top: '14px',
  right: '14px',
  width: '22px',
  height: '22px',
  cursor: 'pointer',
  color: '#A6A6A6',
  '&:hover': {
    color: '#404040',
  },
});

export const ModalIconBadge = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '48px',
  height: '48px',
  borderRadius: '50%',
  background: '#F3F9FA',
  marginBottom: '16px',
});

export const ModalContent = styled(Box)({
  width: '100%',
});

export const ModalTitle = styled(Typography)({
  fontSize: '17px',
  fontWeight: 700,
  lineHeight: 1.45,
  color: '#262626',
  textAlign: 'center',
  wordBreak: 'keep-all',
});

export const ModalDescription = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  width: '100%',
  marginTop: '10px',
});

export const ModalDescText = styled(Typography)({
  fontSize: '13px',
  lineHeight: 1.6,
  color: '#737373',
  textAlign: 'center',
  wordBreak: 'keep-all',
});

export const ModalDescTextSub = styled(Typography)({
  fontSize: '13px',
  lineHeight: 1.6,
  color: '#737373',
  textAlign: 'center',
  wordBreak: 'keep-all',
});

export const ModalButtonWrapper = styled(Box)({
  display: 'flex',
  gap: '8px',
  width: '100%',
  marginTop: '24px',
});

export const ModalCancelButton = styled(Button)({
  flex: 1,
  height: '44px',
  background: '#F2F2F2',
  color: '#404040',
  fontSize: '14px',
  fontWeight: 600,
  borderRadius: '8px',
  boxShadow: 'none',
  '&:hover': {
    background: '#D9D9D9',
    boxShadow: 'none',
  },
});

export const ModalConfirmButton = styled(Button)({
  flex: 1,
  height: '44px',
  background: '#4AB3BC',
  color: '#FFFFFF',
  fontSize: '14px',
  fontWeight: 600,
  borderRadius: '8px',
  boxShadow: 'none',
  '&:hover': {
    background: '#3E9FA7',
    boxShadow: 'none',
  },
});

// Delete Button
export const DeleteButton = styled(Button)({
  borderRadius: '12px',
  paddingLeft: '35px',
  paddingRight: '35px',
  height: '36px',
  background: '#f7f7f7',
  color: 'red',
  border: 'none',
  '&:hover': {
    border: 'none',
    background: '#ef0c23',
    color: '#fff',
  },
});

// Bottom Actions with Delete
export const BottomActionsWithDelete = styled(Box)({
  margin: '24px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

export const BottomActionsRight = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '5px',
});

// ============================================
// New Design - Main Container
// ============================================
export const MainContainer = styled(Box)({
  background: '#FAFBFC',
  borderRadius: '8px',
  boxShadow: '0px 0px 4px 0px rgba(0,0,0,0.16)',
  padding: '32px',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  minHeight: '827px',
});

// ============================================
// New Design - Step Header
// ============================================
export const StepHeaderContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
});

export const StepButtonContainer = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  paddingRight: '42px',
  isolation: 'isolate',
});

export const StepButton = styled(Box, {
  shouldForwardProp: prop => prop !== 'active' && prop !== 'completed',
})(({ active, completed }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  marginRight: '-42px',
  zIndex: active ? 4 : 3,
}));

export const StepButtonBackground = styled('img')({
  position: 'absolute',
  width: '100%',
  height: '48px',
});

export const StepButtonContent = styled(Box, {
  shouldForwardProp: prop => prop !== 'active',
})(({ active }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: active ? '10px 0 10px 23px' : '10px 0 10px 52px',
  zIndex: 1,
}));

export const StepNumber = styled(Box, {
  shouldForwardProp: prop => prop !== 'active',
})(({ active }) => ({
  width: '28px',
  height: '28px',
  borderRadius: '15px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: active ? COLORS.white : COLORS.gray4,
  color: active ? COLORS.primary : COLORS.white,
  fontSize: '18px',
  fontWeight: 600,
}));

export const StepLabel = styled(Typography, {
  shouldForwardProp: prop => prop !== 'active',
})(({ active }) => ({
  fontSize: '20px',
  fontWeight: 600,
  color: active ? COLORS.white : COLORS.gray6,
  whiteSpace: 'nowrap',
}));

export const BackToListButton = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  cursor: 'pointer',
  '& p': {
    fontSize: '20px',
    fontWeight: 500,
    color: COLORS.black,
  },
});

export const BackIconWrapper = styled(Box)({
  width: '24px',
  height: '24px',
  borderRadius: '12px',
  background: COLORS.gray9,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

// ============================================
// New Design - Cards Container
// ============================================
export const CardsContainer = styled(Box)({
  display: 'flex',
  gap: '24px',
  justifyContent: 'space-between',
  width: '100%',
  // padding: '32px',
  alignItems: 'flex-start',
  // background: '#e0e0e0',
  // borderRadius: '10px',
});

// 기본정보 + 출입설정을 세로 한 칼럼으로 묶기 위한 wrapper.
// 다른 카드는 CardsContainer 의 가로 배치를 그대로 사용한다.
export const BasicInfoColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  // 감싸기 전의 기본정보 카드(flex: 1)와 같은 폭 비율을 유지한다.
  flex: 1,
  minWidth: 0,
});

export const InfoCard = styled(Box)({
  background: COLORS.white,
  border: `1px solid ${COLORS.gray2}`,
  borderRadius: '8px',
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  flex: 1,
  minWidth: 0,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
});

export const InfoCardTitle = styled(Typography)({
  fontSize: '18px',
  fontWeight: 600,
  color: COLORS.primary,
});

export const InfoCardHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
});

// ============================================
// New Design - Form Fields
// ============================================
export const FieldGroup = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  width: '100%',
});

export const FieldLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: COLORS.black,
});

export const FieldLabelRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
});

export const FieldHint = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: COLORS.gray4,
});

export const InputField = styled(Box)({
  background: COLORS.white,
  border: `1px solid ${COLORS.gray4}`,
  borderRadius: '4px',
  height: '44px',
  padding: '12px 16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
});

export const InputText = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: COLORS.gray4,
  flex: 1,
});

export const InputUnit = styled(Typography)({
  fontSize: '13px',
  fontWeight: 500,
  color: COLORS.gray6,
  flexShrink: 0,
});

// Gray Background Input Wrapper (for 판매금액)
export const GrayInputWrapper = styled(Box)({
  width: '100%',
  display: 'flex',
  '& > *': {
    flex: 1,
    width: '100%',
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#f2f2f2',
  },
});

// Discount Unit Select (small dropdown inside input)
export const DiscountUnitSelect = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  cursor: 'pointer',
  '& .unit-text': {
    fontSize: '13px',
    fontWeight: 500,
    color: COLORS.gray6,
  },
  '& .unit-arrow': {
    width: '14px',
    height: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: COLORS.gray6,
  },
});

export const HalfWidthContainer = styled(Box)({
  display: 'flex',
  gap: '16px',
  width: '100%',
});

export const HalfWidthField = styled(Box)({
  flex: 1,
  minWidth: 0,
});

// ============================================
// New Design - Radio Buttons (New Style)
// ============================================
export const RadioGroupRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '24px',
  width: '100%',
});

export const RadioOption = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  cursor: 'pointer',
});

export const RadioCircleNew = styled(Box, {
  shouldForwardProp: prop => prop !== 'checked',
})(({ checked }) => ({
  width: '24px',
  height: '24px',
  borderRadius: '12px',
  border: checked ? `2px solid ${COLORS.primary}` : `1px solid ${COLORS.gray2}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: COLORS.white,
}));

export const RadioDotNew = styled(Box)({
  width: '12px',
  height: '12px',
  borderRadius: '6px',
  background: COLORS.primary,
});

export const RadioLabelNew = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: COLORS.black,
});

// ============================================
// New Design - Tags
// ============================================
export const TagsContainer = styled(Box)({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  flexWrap: 'wrap',
});

export const Tag = styled(Box)({
  background: COLORS.primary,
  borderRadius: '999px',
  padding: '6px 12px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const TagText = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: COLORS.white,
  textAlign: 'center',
});

export const TagCloseIcon = styled('span')({
  width: '10px',
  height: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: COLORS.white,
  cursor: 'pointer',
});

// ============================================
// New Design - Toggle Switch
// ============================================
export const ToggleContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});

export const ToggleLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: COLORS.black,
});

export const StyledSwitch = styled(Switch)({
  width: 40,
  height: 20,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 2,
    '&.Mui-checked': {
      transform: 'translateX(20px)',
      '& + .MuiSwitch-track': {
        backgroundColor: COLORS.primary,
        opacity: 1,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    width: 16,
    height: 16,
    backgroundColor: COLORS.white,
  },
  '& .MuiSwitch-track': {
    borderRadius: 10,
    backgroundColor: COLORS.gray2,
    opacity: 1,
  },
});

// ============================================
// New Design - Info Icon
// ============================================
export const InfoIcon = styled(Box)({
  width: '14px',
  height: '14px',
  cursor: 'help',
});

// ============================================
// New Design - Day Selection (New Style)
// ============================================
export const DayButtonsRow = styled(Box)({
  display: 'flex',
  gap: '8px',
  width: '100%',
});

export const DayButtonNew = styled(Box, {
  shouldForwardProp: prop => prop !== 'selected',
})(({ selected }) => ({
  flex: 1,
  height: '44px',
  borderRadius: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  background: selected ? COLORS.primary : COLORS.white,
  border: selected ? 'none' : `1px solid ${COLORS.gray4}`,
  color: selected ? COLORS.white : COLORS.gray6,
  fontSize: '13px',
  fontWeight: 500,
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: COLORS.primary,
  },
}));

// ============================================
// New Design - Time Picker
// ============================================
export const TimePickerContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  width: '100%',
});

export const TimePickerGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  width: '148px',
});

export const TimeSelectSmall = styled(Select)({
  flex: 1,
  height: '44px',
  '& .MuiSelect-select': {
    padding: '12px',
    fontSize: '13px',
    color: COLORS.gray4,
  },
});

export const TimeColon = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: COLORS.gray6,
  width: '12px',
  textAlign: 'center',
});

export const TimeTilde = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: COLORS.gray6,
  width: '16px',
  flexShrink: 0,
  textAlign: 'center',
});

// ============================================
// New Design - Date Picker Row
// ============================================
export const DatePickerRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  gap: '12px',
});

export const DatePickerField = styled(Box)({
  flex: 1,
  minWidth: 0,
  '& .MuiTextField-root': {
    width: '100%',
  },
  '& .MuiOutlinedInput-root': {
    height: '44px',
  },
  '& .MuiOutlinedInput-input': {
    padding: '10px 0 10px 12px',
    fontSize: '14px',
    fontWeight: 500,
    minWidth: 0,
  },
  '& .MuiInputAdornment-root': {
    marginRight: '4px',
    '& .MuiSvgIcon-root': {
      fontSize: '20px',
    },
  },
});

// ============================================
// New Design - Checkbox (New Style)
// ============================================
export const CheckboxRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});

export const CheckboxNew = styled(Box, {
  shouldForwardProp: prop => prop !== 'checked',
})(({ checked }) => ({
  width: '20px',
  height: '20px',
  borderRadius: '2px',
  border: `1.5px solid ${checked ? COLORS.primary : COLORS.gray2}`,
  background: checked ? COLORS.primary : COLORS.white,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
}));

export const CheckboxLabelNew = styled(Typography)({
  fontSize: '13px',
  fontWeight: 500,
  color: COLORS.black,
});

// ============================================
// New Design - Step 2 Styles
// ============================================
export const Step2CardsContainer = styled(Box)({
  display: 'flex',
  gap: '32px',
  width: '100%',
  alignItems: 'flex-start',
});

export const Step2Card = styled(Box, {
  shouldForwardProp: prop => prop !== 'width',
})(({ width }) => ({
  background: COLORS.white,
  border: `1px solid ${COLORS.gray2}`,
  borderRadius: '8px',
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  width: width || '360px',
  flexShrink: 0,
}));

export const AIToggleBoxNew = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  background: COLORS.primaryBg,
  borderRadius: '6px',
  padding: '12px 16px',
  height: '48px',
});

export const AIToggleContent = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const AIIcon = styled(Box)({
  width: '24px',
  height: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: COLORS.primary,
});

export const AIToggleLabelNew = styled(Typography)({
  fontSize: '16px',
  fontWeight: 600,
  color: COLORS.primary,
});

// ============================================
// New Design - Image Upload (Step 2)
// ============================================
export const ImageUploadArea = styled(Box)({
  background: COLORS.white,
  border: `1px solid ${COLORS.gray4}`,
  borderRadius: '8px',
  height: '200px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  cursor: 'pointer',
});

export const ImageUploadIcon = styled(Box)({
  width: '28px',
  height: '28px',
  color: COLORS.primaryLight,
});

export const ImageUploadText = styled(Typography)({
  fontSize: '16px',
  fontWeight: 600,
  color: COLORS.primaryLight,
  textAlign: 'center',
});

export const SmallUploadButton = styled(Box)({
  width: '80px',
  height: '80px',
  border: `1px solid ${COLORS.gray4}`,
  borderRadius: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  background: COLORS.white,
  '&:hover': {
    borderColor: COLORS.primary,
  },
});

// ============================================
// New Design - Curriculum Section
// ============================================
export const CurriculumContainer = styled(Box)({
  background: COLORS.primaryBg,
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

export const CurriculumAddButtonNew = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  cursor: 'pointer',
  color: COLORS.gray8,
  '&:hover': {
    opacity: 0.7,
  },
});

// ============================================
// New Design - Rich Text Editor
// ============================================
export const RichTextContainer = styled(Box)({
  background: COLORS.white,
  border: `1px solid ${COLORS.gray4}`,
  borderRadius: '8px',
  overflow: 'hidden',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: '300px',
  '& .ck-editor': {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  '& .ck-editor__main': {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  '& .ck-content': {
    flex: 1,
    minHeight: '250px',
  },
});

export const RichTextToolbar = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '8px',
  padding: '8px',
  borderBottom: `1px solid ${COLORS.gray2}`,
  flexWrap: 'wrap',
});

export const RichTextArea = styled(Box)({
  flex: 1,
  padding: '16px',
});

// ============================================
// New Design - Bottom Actions (New Style)
// ============================================
export const BottomActionsNew = styled(Box)({
  position: 'fixed',
  bottom: '32px',
  right: '32px',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '8px',
  zIndex: 100,
});

export const CancelButtonNew = styled(Button)({
  width: '100px',
  height: '48px',
  borderRadius: '4px',
  background: COLORS.gray4,
  color: COLORS.white,
  fontSize: '20px',
  fontWeight: 600,
  textTransform: 'uppercase',
  '&:hover': {
    background: COLORS.gray6,
  },
});

export const PrevButtonNew = styled(Button)({
  width: '100px',
  height: '48px',
  borderRadius: '4px',
  border: `1px solid ${COLORS.primary}`,
  background: COLORS.white,
  color: COLORS.primary,
  fontSize: '20px',
  fontWeight: 600,
  textTransform: 'uppercase',
  '&:hover': {
    background: COLORS.primaryBg,
  },
});

export const NextButtonNew = styled(Button)({
  width: '240px',
  height: '48px',
  borderRadius: '4px',
  background: COLORS.primary,
  color: COLORS.white,
  fontSize: '20px',
  fontWeight: 600,
  textTransform: 'uppercase',
  '&:hover': {
    background: COLORS.primaryDark,
  },
  '&:disabled': {
    background: COLORS.gray4,
  },
});

export const SaveButtonNew = styled(Button)({
  width: '240px',
  height: '48px',
  borderRadius: '4px',
  background: COLORS.primary,
  color: COLORS.white,
  fontSize: '20px',
  fontWeight: 600,
  textTransform: 'uppercase',
  '&:hover': {
    background: COLORS.primaryDark,
  },
  '&:disabled': {
    background: COLORS.gray4,
  },
});

// ============================================
// Step Indicator (Arrow Tab Style)
// ============================================
export const StepIndicatorContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  marginBottom: '16px',
});

export const StepTabsWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  paddingRight: '42px',
  isolation: 'isolate',
});

export const StepTab = styled(Box, {
  shouldForwardProp: prop => prop !== 'active' && prop !== 'zIndex',
})(({ active, zIndex }) => ({
  position: 'relative',
  display: 'inline-grid',
  gridTemplateRows: 'max-content',
  gridTemplateColumns: 'max-content',
  placeItems: 'start',
  marginRight: '-42px',
  zIndex: zIndex || 3,
}));

export const StepTabShape = styled(Box, {
  shouldForwardProp: prop => prop !== 'active',
})(({ active }) => ({
  gridRow: 1,
  gridColumn: 1,
  width: active ? '160px' : '200px',
  height: '48px',
  position: 'relative',
  background: active ? COLORS.primary : COLORS.gray1,
  clipPath: 'polygon(0 0, calc(100% - 20px) 0, 100% 50%, calc(100% - 20px) 100%, 0 100%, 20px 50%)',
  '&:first-of-type': {
    clipPath: 'polygon(0 0, calc(100% - 20px) 0, 100% 50%, calc(100% - 20px) 100%, 0 100%)',
  },
}));

export const StepTabShapeFirst = styled(Box, {
  shouldForwardProp: prop => prop !== 'active',
})(({ active }) => ({
  gridRow: 1,
  gridColumn: 1,
  width: '160px',
  height: '48px',
  position: 'relative',
  background: active ? COLORS.primary : COLORS.gray1,
  clipPath: 'polygon(0 0, calc(100% - 20px) 0, 100% 50%, calc(100% - 20px) 100%, 0 100%)',
}));

export const StepTabShapeSecond = styled(Box, {
  shouldForwardProp: prop => prop !== 'active',
})(({ active }) => ({
  gridRow: 1,
  gridColumn: 1,
  width: '200px',
  height: '48px',
  position: 'relative',
  background: active ? COLORS.primary : COLORS.gray1,
  clipPath: 'polygon(0 0, calc(100% - 20px) 0, 100% 50%, calc(100% - 20px) 100%, 0 100%, 20px 50%)',
}));

export const StepTabContent = styled(Box, {
  shouldForwardProp: prop => prop !== 'active' && prop !== 'isFirst',
})(({ active, isFirst }) => ({
  gridRow: 1,
  gridColumn: 1,
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginLeft: isFirst ? '23px' : '52px',
  marginTop: '10px',
  position: 'relative',
  zIndex: 1,
}));

export const StepTabNumber = styled(Box, {
  shouldForwardProp: prop => prop !== 'active',
})(({ active }) => ({
  width: '28px',
  height: '28px',
  borderRadius: '15px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: active ? COLORS.white : COLORS.gray4,
  fontFamily: 'Pretendard, sans-serif',
  fontSize: '18px',
  fontWeight: 600,
  color: active ? COLORS.primary : COLORS.white,
}));

export const StepTabLabel = styled(Typography, {
  shouldForwardProp: prop => prop !== 'active',
})(({ active }) => ({
  fontFamily: 'Pretendard, sans-serif',
  fontSize: '20px',
  fontWeight: 600,
  color: active ? COLORS.white : COLORS.gray6,
  whiteSpace: 'nowrap',
}));

export const BackToListLink = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  cursor: 'pointer',
  textDecoration: 'none',
});

export const BackToListIcon = styled(Box)({
  width: '24px',
  height: '24px',
  borderRadius: '12px',
  background: COLORS.gray9,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export const BackToListText = styled(Typography)({
  fontFamily: 'Pretendard, sans-serif',
  fontSize: '20px',
  fontWeight: 500,
  color: COLORS.black,
  whiteSpace: 'nowrap',
});

// ============================================
// Sport Tag Input (Multi-select with tags)
// ============================================
export const SportTagInputContainer = styled(Box)({
  background: COLORS.white,
  border: `1px solid ${COLORS.gray4}`,
  borderRadius: '4px',
  minHeight: '44px',
  padding: '8px 16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  cursor: 'pointer',
  width: '100%',
  '&:hover': {
    borderColor: COLORS.primary,
  },
});

export const SportTagsWrapper = styled(Box)({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  flexWrap: 'wrap',
  flex: 1,
});

export const SportTag = styled(Box)({
  background: COLORS.primary,
  borderRadius: '999px',
  padding: '6px 12px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const SportTagText = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: COLORS.white,
  textAlign: 'center',
  whiteSpace: 'nowrap',
});

export const SportTagCloseIcon = styled(Box)({
  width: '10px',
  height: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: COLORS.white,
  cursor: 'pointer',
  '&:hover': {
    opacity: 0.8,
  },
});

export const SportTagPlaceholder = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: COLORS.gray4,
});

export const SportTagDropdownIcon = styled(Box)({
  width: '12px',
  height: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  color: COLORS.gray4,
});

// ============================================
// Info Card Header Row (Title + Hint inline)
// ============================================
export const InfoCardHeaderRow = styled(Box)({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  width: '100%',
  whiteSpace: 'nowrap',
});

export const InfoCardHint = styled(Typography)({
  fontSize: '12px',
  fontWeight: 500,
  color: COLORS.gray4,
});

// ============================================
// Field Label with Info Icon
// ============================================
export const FieldLabelWithInfo = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});

export const InfoIconSmall = styled(Box)({
  width: '14px',
  height: '14px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'help',
  color: COLORS.gray4,
});

// ============================================
// Input with Unit Inside (like Figma design)
// ============================================
export const InputWithUnitContainer = styled(Box)({
  background: COLORS.white,
  border: `1px solid ${COLORS.gray4}`,
  borderRadius: '4px',
  height: '44px',
  padding: '12px 16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  width: '100%',
  '& .MuiInputBase-root': {
    flex: 1,
    '& fieldset': {
      border: 'none',
    },
    '& input': {
      padding: 0,
      fontSize: '14px',
      fontWeight: 500,
      color: COLORS.black,
      '&::placeholder': {
        color: COLORS.gray4,
      },
    },
  },
});

export const InputUnitInside = styled(Typography)({
  fontSize: '13px',
  fontWeight: 500,
  color: COLORS.gray6,
  whiteSpace: 'nowrap',
  flexShrink: 0,
});

// ============================================
// Usage Period Select (이용기간)
// ============================================
export const UsagePeriodContainer = styled(Box)({
  width: '148px',
});

export const UsagePeriodSelect = styled(Box)({
  background: COLORS.white,
  border: `1px solid ${COLORS.gray4}`,
  borderRadius: '4px',
  height: '44px',
  padding: '12px 16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '4px',
  cursor: 'pointer',
  '&:hover': {
    borderColor: COLORS.primary,
  },
});

// 이용기간: input + 단위 select 가 하나의 박스 안에 합쳐진 컴팩트 스타일
export const UsagePeriodInputBox = styled(Box, {
  shouldForwardProp: prop => prop !== 'hasError',
})(({ hasError }) => ({
  width: '148px',
  height: '44px',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '0 12px 0 16px',
  background: COLORS.white,
  border: hasError ? `2px solid ${COLORS.error}` : `1px solid ${COLORS.gray4}`,
  borderRadius: '4px',
  transition: 'border-color 0.2s',
  animation: hasError ? `${errorPulseGlow} 1.5s ease-in-out infinite` : 'none',
  '& .usage-period-input': {
    flex: 1,
    minWidth: 0,
    '& .MuiOutlinedInput-root': {
      padding: 0,
      '& fieldset': { border: 'none' },
      '& input': {
        padding: 0,
        fontSize: '14px',
        fontWeight: 500,
        color: COLORS.black,
        '&::placeholder': { color: COLORS.gray4, opacity: 1 },
      },
    },
  },
  '& .usage-period-select': {
    height: 'auto',
    '&::before, &::after': { display: 'none' },
    '& .MuiSelect-select': {
      padding: '0 20px 0 0 !important',
      fontSize: '13px',
      fontWeight: 500,
      color: COLORS.gray6,
      minHeight: 'unset',
    },
    '& .MuiSelect-icon': {
      right: 0,
      color: COLORS.primary,
      fontSize: '18px',
    },
  },
}));

// ============================================
// 복사 모드 배너 — 편집 화면과 구분되도록 복사 진행 중임을 알린다
// ============================================
export const CopyModeBanner = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  width: '100%',
  padding: '12px 16px',
  marginBottom: '16px',
  borderRadius: '6px',
  borderLeft: `4px solid ${COLORS.primary}`,
  background: COLORS.primaryBg,
});

export const CopyModeBannerTitle = styled(Typography)({
  fontSize: '14px',
  fontWeight: 700,
  color: COLORS.primaryDark,
  whiteSpace: 'nowrap',
});

export const CopyModeBannerText = styled(Typography)({
  fontSize: '13px',
  fontWeight: 400,
  color: COLORS.gray8,
});

// 생성 화면 우측 액션 묶음 — 불러오기 버튼과 목록으로 링크를 세로 중앙으로 나란히 둔다
export const HeaderActionGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

export const LoadProductButton = styled(Button)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  height: '36px',
  padding: '0 16px',
  background: '#FFFFFF',
  color: '#3E9FA7',
  fontSize: '14px',
  fontWeight: 600,
  lineHeight: 1,
  whiteSpace: 'nowrap',
  border: '1px solid #4AB3BC',
  borderRadius: '8px',
  boxShadow: 'none',
  '&:hover': {
    background: '#F3F9FA',
    boxShadow: 'none',
  },
});
